import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useTranslation } from '../../../contexts/TranslationContext';
import {
  maxRainfallLevelSelector,
  maxWaterLevelSelector,
} from '../../../models/selectors/device_selectors';
import { unix_timestamp_to_date_time } from '../../../utils/helper_functions';
import { DeviceInfoSection } from './DeviceInfoSection';
import { useDeviceDataPeriod } from '../../../hooks/useDeviceDataPeriod';
import dayjs from 'dayjs';
import { Link } from '@mui/material';
import { periodTypeAtom } from '../../../models/atoms/device_atoms';

type Props = {
  showWaterLevel: boolean;
  showRainfallLevel: boolean;
  maxHeight: number;
};

export function DeviceDataRangeStats({
  showWaterLevel,
  showRainfallLevel,
  maxHeight,
}: Props) {
  const { t, distance, language } = useTranslation();
  const maxWaterLevel = useRecoilValue(maxWaterLevelSelector);
  const maxRainfallLevel = useRecoilValue(maxRainfallLevelSelector);
  const { setPeriodStartDate, setPeriodEndDate } = useDeviceDataPeriod();
  const setPeriodType = useSetRecoilState(periodTypeAtom);

  const handleDateClick = (timestamp: number) => {
    const date = dayjs.unix(timestamp);
    setPeriodStartDate(date.startOf('day'));
    setPeriodEndDate(date.endOf('day'));
    setPeriodType('day');
  };

  const maxFillPercentage =
    maxWaterLevel === null
      ? null
      : ((maxWaterLevel.value / maxHeight) * 100).toFixed(1);

  const maxLevelLabel =
    maxWaterLevel !== null && maxFillPercentage !== null ? (
      <>
        {`${distance.show(maxWaterLevel.value)} (${maxFillPercentage}%) ${t(
          'at',
        )} `}
        <Link
          component="button"
          variant="inherit"
          onClick={() => handleDateClick(maxWaterLevel.timestamp)}
          sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}
        >
          {unix_timestamp_to_date_time(
            maxWaterLevel.timestamp,
            true,
            true,
            false,
            language,
          )}
        </Link>
      </>
    ) : (
      t('No data')
    );

  const maxRainfallLabel = maxRainfallLevel ? (
    <>
      {`${distance.show(maxRainfallLevel.value)} ${t('at')} `}
      <Link
        component="button"
        variant="inherit"
        onClick={() => handleDateClick(maxRainfallLevel.timestamp)}
        sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}
      >
        {unix_timestamp_to_date_time(
          maxRainfallLevel.timestamp,
          true,
          true,
          false,
          language,
        )}
      </Link>
    </>
  ) : (
    t('No data')
  );

  return (
    <div style={{ marginBottom: '1rem' }}>
      {showWaterLevel && maxWaterLevel && maxWaterLevel.value > 0 ? (
        <DeviceInfoSection.TextEntry
          label={t('Max water level in selected range:')}
          text={maxLevelLabel}
        />
      ) : null}
      {showRainfallLevel && maxRainfallLevel && maxRainfallLevel.value > 0 ? (
        <DeviceInfoSection.TextEntry
          label={t('Max rainfall in selected range:')}
          text={maxRainfallLabel}
        />
      ) : null}
    </div>
  );
}
